import { portalText } from "../i18n/portal.js";
/**
 * What Contact lists, New campaign, Campaign and Survey results render (P14-01/02/03).
 *
 * The view types live here rather than in `gateway.ts` because there are four screens' worth
 * of them and `gateway.ts` is already the file every unit touches; `gateway.ts` re-exports
 * them, so a screen still imports from one place. The console's copy for outbound lives here
 * too — how a skip reads, what a question kind is called on screen, what an estimate band
 * says. Copy, never configuration: nothing here has a value a deployment could get wrong.
 *
 * **No field is invented and no absence is papered over.** Three in particular:
 *
 *   `recipientTimeZone`      the contact's own zone. `null` is a real state, and a campaign
 *                            skips those rows rather than ringing them in the workspace's
 *                            zone (P14-02).
 *   `estimate`               a BAND with its assumption beside it, never a single figure. A
 *                            campaign that has not run has no measured answer rate.
 *   `completionBasisPoint`   `null` when a survey has no conversations yet. A rate of nothing
 *                            is not 0%. (AMENDED 2026-10-03: the round `delta` this line used to
 *                            describe went with the rounds.)
 */
import type { Money, TalkRateKind } from "@wetalk/domain";
import { callingRangeGroupsOf, type CallingRange } from "@wetalk/view-model/calling-range";

import { readRecord, readWholeNumber } from "./decode.js";

// ---------------------------------------------------------------------------------------
// Contact lists (P14-01)
// ---------------------------------------------------------------------------------------

/** Where a list comes from, as the three-way segmented control offers it. */
export type ContactListSourceView = "csv_upload" | "api" | "paste";

/** The tag on a list card. `syncing` is only reachable for an `api` source. */
export type ContactListStateView = "in_use" | "ready" | "syncing" | "failed";

/** What a detected column may be mapped to. */
export type ContactColumnRoleView =
  | "display_name"
  | "e164"
  | "language_code"
  | "time_zone"
  | "personal_detail"
  | "ignored";

export interface ContactColumnView {
  readonly sourceName: string;
  readonly role: ContactColumnRoleView;
  /**
   * Whether the agent may say this value out loud.
   *
   * On an inspection it is the API's proposal. Once the member chooses a role, the screen sets
   * it from that role (`spokenFor`, review 2026-09-25 CL #107): a phone number, a language or a
   * time zone is never spoken, whatever the column was first proposed as — the API refuses
   * `spoken: true` on those roles.
   */
  readonly spoken: boolean;
  readonly sample: readonly string[];
}

/** Why a row could not be read, as the API codes it (`unknown_language` since 2026-10-10). */
export type ContactListUnreadableCodeView =
  | "missing_number"
  | "invalid_number"
  | "duplicate_number"
  | "too_many_fields"
  | "unknown_language";

/** A row the inspection could not read: its line, whose it is (when a column is the name), why. */
export interface ContactListUnreadableRowView {
  readonly line: number;
  readonly displayName: string | null;
  /** The API's code, for a screen that words a problem its own way (the paste check, 2026-10-10). */
  readonly code: ContactListUnreadableCodeView;
  /** Worded: "no phone number on this row". */
  readonly problem: string;
}

/**
 * A value that is present but cannot be used (review 2026-09-25, CL #118). A row with an unknown time zone is kept;
 * since 2026-10-10 a row with an unknown language is not — it is also an unreadable row.
 */
export interface ContactListUnrecognisedValueView {
  readonly line: number;
  readonly kind: "unknown_time_zone" | "unknown_language";
  /** The value as written. */
  readonly value: string;
}

/** What the API found in an upload before anything was saved. */
export interface ContactListInspectionView {
  readonly rowCount: number;
  /** How many people saving would keep (under the mapping the inspection was asked with). */
  readonly readableCount: number;
  readonly column: readonly ContactColumnView[];
  readonly unreadableRow: readonly ContactListUnreadableRowView[];
  readonly unrecognisedValue: readonly ContactListUnrecognisedValueView[];
}

/**
 * One of the rules the customer may turn off.
 *
 * Suppression is **not** one of them. The Compliance screen says campaigns "cannot be made to
 * include" a suppressed number, and the reason that is true is that the dialler's decision
 * function reads no suppression option — so this list carries two rules, not three.
 */
export interface ContactListRuleView {
  readonly code: "skip_called_within_day" | "skip_after_no_answer";
  readonly title: string;
  readonly dek: string;
  readonly enabled: boolean;
  readonly value: number;
}

export interface ContactListRowView {
  readonly contactListId: string;
  readonly name: string;
  readonly source: ContactListSourceView;
  readonly state: ContactListStateView;
  readonly personCount: number;
  /** "1,840 people · uploaded 14 Sep" — already composed, because the shape varies by source. */
  readonly metaText: string;
  /** "uploaded today 17:36" — which of two lists with one name this is (CL #137). */
  readonly arrivalText: string;
  /** "Used by Autumn customer survey · columns: name, phone, last_order, language" */
  readonly detailText: string;
  readonly iconName: string;
  readonly columnName: readonly string[];
  /** The two rules as saved (CL #122); `null` is off. */
  readonly skipCalledWithinDay: number | null;
  readonly skipAfterNoAnswer: number | null;
  /** Rows skipped when the list was saved; `null` for a list saved before they were recorded. */
  readonly skippedRowCount: number | null;
  /** Every campaign that references the list, whatever its state: above zero, it cannot be removed. */
  readonly campaignCount: number;
  /** The zone given to people whose row had none, or `null`. */
  readonly assumedTimeZone: string | null;
}

/** One list on its own page (CL #121): the card's facts plus the skipped rows. */
export interface ContactListDetailView extends ContactListRowView {
  /** `null` for a list saved before skipped rows were recorded. */
  readonly skippedRow: readonly { readonly line: number; readonly problem: string }[] | null;
}

/** One person on a list, as saved. */
export interface ContactListPersonView {
  readonly contactId: string;
  readonly displayName: string | null;
  readonly e164: string;
  readonly languageCode: string | null;
  /** The zone campaigns call them in (their row's, their number's exact zone or the list's choice), or `null`. */
  readonly timeZone: string | null;
  /**
   * 2026-10-10: when `timeZone` is `null` and the number spans several zones, every zone it could be in (campaigns
   * call only while the hours hold in all of them); empty otherwise.
   */
  readonly possibleTimeZone: readonly string[];
  readonly suppressed: boolean;
}

export interface ContactListPersonPageView {
  readonly person: readonly ContactListPersonView[];
  readonly nextCursor: string | null;
}

/** A rename or a rule change (CL #121, CL #122). A field left out is unchanged; `null` is off. */
export interface ContactListChangeView {
  readonly name?: string;
  readonly skipCalledWithinDay?: number | null;
  readonly skipAfterNoAnswer?: number | null;
}

/**
 * People to add to a saved list, or to replace everyone on it with (fix round R, CL #121): the
 * same source, text, mapping and assumed zone a new list is saved with. The list keeps its name
 * and its rules.
 */
export interface ContactListPeopleDraftView {
  readonly source: "csv_upload" | "paste";
  readonly sourceHandle: string;
  readonly column: readonly ContactColumnView[];
  readonly assumedTimeZone: string | null;
}

/** What adding or replacing did (fix round R, CL #121). */
export interface ContactListPeopleResultView {
  readonly row: ContactListRowView;
  readonly addedCount: number;
  /** Already on the list (adding only); each is also among `unreadableRow`. */
  readonly duplicateCount: number;
  /** On the list before a replace; 0 when adding. */
  readonly removedCount: number;
  readonly unreadableRow: readonly ContactListUnreadableRowView[];
}

export interface ContactListPageView {
  readonly row: readonly ContactListRowView[];
  readonly listCount: number;
  readonly personCount: number;
  /** How many of this account's numbers are suppressed. Shown in the kicker. */
  readonly suppressedCount: number;
}

export interface ContactListDraftView {
  readonly name: string;
  readonly source: ContactListSourceView;
  /** The upload handle, the API URL or the pasted text, depending on `source`. */
  readonly sourceHandle: string;
  readonly column: readonly ContactColumnView[];
  readonly skipCalledWithinDay: number;
  readonly skipAfterNoAnswer: number;
  /**
   * The zone for everyone whose row gives none (CL #118) — a choice the member makes on screen,
   * never supplied for them — or `null`, and those people are skipped by the dialler.
   */
  readonly assumedTimeZone: string | null;
}

// ---------------------------------------------------------------------------------------
// Campaigns (P14-02)
// ---------------------------------------------------------------------------------------

export type CampaignStateView =
  | "draft"
  | "scheduled"
  | "running"
  | "paused"
  | "finished"
  | "cancelled";

export type CampaignQuestionKindView = "scale" | "choice" | "open";

/** How a campaign treats somebody who already has a survey on file from this agent. */
export type ReturningPersonTreatmentView =
  | "same_questions"
  | "different_questions"
  | "skip_them";

/**
 * Which set a question belongs to: `primary` is asked of everyone; `returning` only of people
 * already surveyed, when the campaign asks them different questions (`different_questions`).
 */
export type CampaignQuestionSetView = "primary" | "returning";

export interface CampaignQuestionRowView {
  readonly campaignQuestionId: string;
  /** Counted within its own set: the returning set starts again at 1. */
  readonly ordinal: number;
  readonly questionSet: CampaignQuestionSetView;
  readonly text: string;
  readonly kind: CampaignQuestionKindView;
  readonly answeredCount: number;
  readonly selectionMax?: number;
}

/**
 * The campaign outcome codes the API's `CampaignOutcome.code` names, in the order the bars are drawn. `voicemail`
 * (plan-031 VMD-WAPP, design §42.10): the attempt was answered by a voicemail and hung up on at once — retried like
 * `no_answer`, never "connected". A tuple, so a later outcome is one entry here and one label in the slice's map.
 */
export const CAMPAIGN_OUTCOME_CODE = [
  "completed",
  // plan-033 WAPP-HUP (design §44.10): the API reports `partial` as its two kinds of hang-up — nothing answered, or at
  // least one answer. AMENDED 2026-10-29 (decoder tightened, design §44.12): `partial` is no longer a campaign outcome
  // code here — the api has not returned it since plan-033 went live (2026-10-28), so a row naming it is malformed.
  // (The stored attempt outcome and a survey respondent's `outcome` still say `partial`; that is another vocabulary.)
  "hung_up_mid_way",
  "hung_up_before_first_question",
  "declined",
  "no_answer",
  "voicemail",
] as const;
export type CampaignOutcomeCodeView = (typeof CAMPAIGN_OUTCOME_CODE)[number];

/**
 * The voicemail detection switch as the campaign read reports it (plan-031, design §42.11; `voicemail_detection`):
 * `on` — a voicemail is detected and hung up on at once; `shadow` — detection only listens, nobody is hung up on;
 * `off` — none. It describes the platform, never a setting of the campaign, and it changes words only: the console
 * says a voicemail is detected (and retried) only while it reads `on`.
 */
export const VOICEMAIL_DETECTION_MODE_VIEW = ["off", "shadow", "on"] as const;
export type VoicemailDetectionModeView = (typeof VOICEMAIL_DETECTION_MODE_VIEW)[number];

export interface CampaignOutcomeShareView {
  readonly code: CampaignOutcomeCodeView;
  readonly label: string;
  readonly count: number;
  /** Of the calls made, not of the list. Hundredths of a percent, so nothing is a float. */
  readonly shareBasisPoint: number;
}

/**
 * The settings panel.
 *
 * `timeZoneNote` is not decoration: the hours are the RECIPIENT's, and a screen that printed
 * "10:00–19:00" beside a workspace time zone would be stating the opposite of what happens.
 */
export interface CampaignScheduleView {
  /** 2026-10-30: every calling range per weekday, sorted; the three fields after it are their summary. */
  readonly callingHours: readonly CallingRangeView[];
  /** ISO weekday numbers, Monday = 1: the days with a range. */
  readonly dayOfWeek: readonly number[];
  /** The earliest start and the latest end of the ranges. */
  readonly callFromLocalTime: string;
  readonly callToLocalTime: string;
  readonly windowText: string;
  readonly timeZoneNote: string;
  readonly simultaneousCall: number;
  /** The most `simultaneousCall` may be now: the lowest of the three figures below (campaign fix-up 2026-10-28). */
  readonly simultaneousCallCeiling: number;
  /** The organization's concurrency entitlement. */
  readonly entitlementCeiling: number;
  /** The agent's own line limit, else its live version's (ADR 0045); `null` when it has no live version. */
  readonly voiceAgentChannelUnitLimit: number | null;
  /** The workspace's own line limit (campaign fix-up 2026-10-28); `null` when it has none. */
  readonly workspaceChannelUnitLimit: number | null;
  readonly retryText: string;
  readonly paceText: string;
}

export interface CampaignCostView {
  readonly spent: Money;
  /** Whole seconds of talk so far — shown through `talkTimeText`, never pre-rounded to minutes. */
  readonly talkSecond: number;
  /** `null` until at least one call has completed; never a division by zero shown as zero. */
  readonly perCompleted: Money | null;
  readonly projected: Money;
  /**
   * plan-017 (Q5, OQ-1): the OUTBOUND kind of the route the campaign dials on — `sip_trunk_outbound` through the
   * organization's own trunk, else `carrier_leg_outbound` — and its rate in force now, a minute.
   */
  readonly talkRateKind: TalkRateKind;
  readonly talkRatePerMinute: Money;
  /** Conversations with billed seconds and no price, left out of `spent` (never guessed). */
  readonly priceUnavailableCount: number;
}

export interface CampaignRowView {
  readonly campaignId: string;
  readonly name: string;
  readonly state: CampaignStateView;
  readonly contactListName: string;
  readonly personCount: number;
  readonly calledCount: number;
  readonly completedCount: number;
  readonly startedText: string;
  readonly etaText: string;
}

export interface CampaignView {
  readonly campaignId: string;
  readonly voiceAgentId: string;
  readonly name: string;
  readonly state: CampaignStateView;
  readonly contactListName: string;
  readonly startedText: string;
  readonly etaText: string;
  readonly personCount: number;
  readonly calledCount: number;
  readonly completedCount: number;
  readonly connectedCount: number;
  /** plan-031: people whose latest dialled attempt a voicemail answered and was hung up on. Never in `connectedCount`. */
  readonly voicemailCount: number;
  /**
   * plan-033 WAPP-RET (design §44.10): people whose counted attempt is a call-back — they rang the campaign's number back
   * and the conversation was joined. Required since 2026-10-29 (decoder tightened, design §44.12): the api has sent it
   * since plan-033 went live (2026-10-28). The line "of which called back" shows only when it is above 0.
   */
  readonly calledBackCount: number;
  /** plan-031: whether voicemails are detected right now (the platform's switch, from the campaign read). */
  readonly voicemailDetection: VoicemailDetectionModeView;
  readonly questionSetText: string;
  readonly question: readonly CampaignQuestionRowView[];
  readonly outcome: readonly CampaignOutcomeShareView[];
  readonly schedule: CampaignScheduleView;
  readonly cost: CampaignCostView;
  /** `null` when this campaign has produced no survey round yet. */
  readonly surveyRoundId: string | null;
}

export interface CampaignQuestionDraftView {
  readonly ordinal: number;
  readonly code: string;
  readonly text: string;
  readonly kind: CampaignQuestionKindView;
}

export interface CampaignDraftView {
  readonly voiceAgentId: string;
  readonly contactListId: string;
  readonly name: string;
  readonly question: readonly CampaignQuestionDraftView[];
  readonly returningPerson: ReturningPersonTreatmentView;
  readonly returningQuestion: readonly CampaignQuestionDraftView[] | null;
  readonly attemptsPerPerson: number;
  readonly waitBetweenAttemptMinute: number;
  /**
   * 2026-10-30: the calling ranges per weekday, in the RECIPIENT's zone — there is deliberately no zone field beside
   * them. Any number a day, each day its own; sent as `calling_hours`.
   */
  readonly callingHours: readonly CallingRangeView[];
  readonly simultaneousCall: number;
  /** `null` is "as soon as it is ready". */
  readonly startsAt: string | null;
  /**
   * The caller-ID number it would dial from, when one is chosen. plan-017 (Q5): the estimate sends it, so its band is
   * priced at that route's outbound rate (a number on the organization's own trunk: `sip_trunk_outbound`).
   */
  readonly callerIdPhoneNumberId?: string;
}

/**
 * What a New campaign opens with (`suggestCampaignDraft`). Only the saved list: every other field
 * already opens on the design's defaults, and the name stays empty behind its placeholder.
 */
export interface CampaignDraftSuggestionView {
  readonly contactListId: string;
}

export interface CampaignEstimateView {
  /** The people who would be called: not on do not call, and with a time zone (CL #118). */
  readonly personCount: number;
  /** People skipped because their row gives no time zone (CL #118). */
  readonly noTimeZoneCount: number;
  readonly dayCount: number;
  readonly low: Money;
  readonly high: Money;
  readonly assumption: string;
  /** The most `simultaneous_call` may be: the lowest of the three figures below (campaign fix-up 2026-10-28). */
  readonly simultaneousCallCeiling: number;
  /** The organization's concurrency entitlement. */
  readonly entitlementCeiling: number;
  /** The agent's own line limit, else its live version's (ADR 0045); `null` when it has no live version. */
  readonly voiceAgentChannelUnitLimit: number | null;
  /** The workspace's own line limit (campaign fix-up 2026-10-28); `null` when it has none. */
  readonly workspaceChannelUnitLimit: number | null;
  /** What the concurrency slider says under it, which changes at the ceiling. */
  readonly concurrencyNote: string;
  /**
   * plan-017 (Q5, OQ-1): what the band is priced at — the OUTBOUND rate of the route it would dial on
   * (`sip_trunk_outbound` from a number on the organization's own trunk, else `carrier_leg_outbound`), a minute.
   */
  readonly talkRateKind: TalkRateKind;
  readonly talkRatePerMinute: Money;
  /**
   * plan-031 VMD-WAPP: the voicemail detection switch, so the New campaign form's voicemail and retry sentences follow
   * it as the Campaign screen's do (design §42.11).
   */
  readonly voicemailDetection: VoicemailDetectionModeView;
}

/** AC-31's reasons, exactly as the domain enumerates them (`already_surveyed` since 2026-10-05). */
export type CampaignSkipReasonView =
  | "suppressed"
  | "already_surveyed"
  | "called_within_window"
  | "no_answer_limit"
  | "outside_local_calling_hours"
  | "unknown_time_zone"
  | "invalid_number";

export interface CampaignSkipRowView {
  readonly campaignAttemptId: string;
  readonly e164: string;
  readonly displayName: string | null;
  readonly skipReason: CampaignSkipReasonView;
  readonly whenText: string;
}

export interface CampaignSkipPageView {
  readonly row: readonly CampaignSkipRowView[];
  readonly nextCursor: string | null;
  readonly totalCount: number;
  /** Per reason, so the screen can say WHY a campaign is reaching fewer people than expected. */
  readonly countByReason: Readonly<Record<CampaignSkipReasonView, number>>;
}

// ---------------------------------------------------------------------------------------
// Survey results (P14-03; AMENDED 2026-10-03: read from the interviewer's own answers)
// ---------------------------------------------------------------------------------------
//
// AMENDED 2026-10-03 (design §16.7.2 AS BUILT 2026-10-03). The round-based views of P14-03 are
// gone: nothing ever wrote a survey round, and the survey interviewer files `survey_answer`. These
// views carry what `getVoiceAgentSurveyResult`, `listVoiceAgentSurveyRespondents`,
// `createVoiceAgentSurveyExport` and `getConversationSurveyAnswer` answer: facts, counts and basis
// points; the screens word every sentence.

/** How a survey conversation ended. `no_outcome`: still running, or none filed. */
export type SurveyRespondentOutcomeView = "completed" | "partial" | "declined" | "no_answer" | "no_outcome";

/**
 * plan-033 WAPP-HUP (design §44.9): how a `partial` survey conversation ended — before the first question (nothing
 * answered) or mid-way (at least one answer). Reported by the API from plan-033 on; `null` before it, and for every
 * other outcome.
 */
export type SurveyHangUpKindView = "hung_up_before_first_question" | "hung_up_mid_way";

/** The window a results page reads, as History's and Analytics' are offered. */
export type SurveyResultRangeView = "24h" | "7d" | "14d" | "30d" | "90d" | "all";

export type SurveyVariantView = "A" | "B";

/** What an answer was filed as (ADR 0053): `statement` for a scale question; v1's `emotion` / `source`. */
export type SurveyQuestionKindView = "statement" | "emotion" | "source" | "choice" | "open" | "number";

/** ADR 0053: a question's type; `null` when no questionnaire describes it. */
export type SurveyQuestionTypeView = "scale" | "choice" | "open" | "number";

/** A number question's bounds, as its questionnaire states them. */
export interface SurveyNumberRangeView {
  readonly min: number | null;
  readonly max: number | null;
  readonly integer: boolean;
  /** Language code → the unit as that language words it; empty when none. */
  readonly unit: Readonly<Record<string, string>>;
}

/** A number question's numbers in scope, as exact decimal text from the API. */
export interface SurveyNumberSummaryView {
  readonly count: number;
  readonly average: string;
  readonly minimum: string;
  readonly maximum: string;
}

/** What narrows a results page. Every field is in the URL, so a filtered page is a link. */
export interface SurveyResultFilterView {
  readonly range: SurveyResultRangeView;
  /** `false` reads real conversations only — the default; `true` adds the test ones, each marked. */
  readonly includeIsTest: boolean;
  readonly campaignId: string | null;
  readonly languageCode: string | null;
  readonly variant: SurveyVariantView | null;
}

export interface SurveyAnswerOptionView {
  /** The recorded value: `strongly_agree`, `yes`, … or an emotion or source id. Worded by the screen. */
  readonly answer: string;
  readonly count: number;
  /** Of the question's own answers; from the API, never divided here. */
  readonly shareBasisPoint: number;
  readonly confidentCount: number;
  readonly unsureCount: number;
  /** Emotion and source: respondents who named this option (one may name several). */
  readonly mentionCount: number;
  readonly label?: Readonly<Record<string, string>>;
}

export interface SurveyQuestionResultView {
  readonly questionCode: string;
  readonly instrumentCode: string;
  readonly questionKind: SurveyQuestionKindView;
  readonly variant: SurveyVariantView | null;
  readonly ordinal: number;
  /** Language code → the question as that language's questionnaire words it. Empty when none does. */
  readonly text: Readonly<Record<string, string>>;
  readonly answeredCount: number;
  readonly selectionMax?: number;
  readonly questionType?: SurveyQuestionTypeView | null;
  readonly numberRange?: SurveyNumberRangeView;
  readonly number?: SurveyNumberSummaryView;
  readonly option: readonly SurveyAnswerOptionView[];
}

export interface SurveyResultView {
  readonly conversationCount: number;
  readonly completedCount: number;
  readonly partialCount: number;
  /**
   * plan-033 WAPP-HUP: `partialCount` split — nothing answered, or at least one answer. Required since 2026-10-29
   * (decoder tightened, design §44.12): the api has reported both since plan-033 went live (2026-10-28), so the page
   * always shows Partial as its two kinds.
   */
  readonly hungUpBeforeFirstQuestionCount: number;
  readonly hungUpMidWayCount: number;
  readonly declinedCount: number;
  readonly noAnswerCount: number;
  readonly noOutcomeCount: number;
  /** `null` with no conversations: there is nothing to divide by, which is not 0%. */
  readonly completionBasisPoint: number | null;
  readonly isTestCount: number;
  readonly hiddenIsTestCount: number;
  readonly repeat: {
    readonly answeredTwiceCount: number;
    readonly answeredThreeOrMoreCount: number;
  };
  readonly variant: readonly { readonly variant: SurveyVariantView; readonly conversationCount: number }[];
  /** Every language of the window, most spoken first — whichever is chosen. */
  readonly language: readonly { readonly languageCode: string; readonly conversationCount: number }[];
  readonly campaign: readonly { readonly campaignId: string; readonly name: string; readonly conversationCount: number }[];
  readonly question: readonly SurveyQuestionResultView[];
}

export interface SurveyGivenAnswerView {
  readonly questionCode: string;
  readonly questionKind: SurveyQuestionKindView;
  readonly answer: string;
  readonly mentioned: readonly string[];
  readonly optionLabel?: Readonly<Record<string, string>>;
  /** ADR 0053: an open answer's own words — only for a person who may read transcripts. */
  readonly openText?: string;
  readonly confident: boolean | null;
  readonly languageCode: string;
}

export interface SurveyRespondentRowView {
  readonly conversationId: string;
  /** "Today 14:02", worded by the slice from the conversation's start. */
  readonly startedText: string;
  readonly outcome: SurveyRespondentOutcomeView;
  /** plan-033 WAPP-HUP: for a `partial` respondent, which kind of hang-up; `null` otherwise. */
  readonly hangUpKind: SurveyHangUpKindView | null;
  readonly languageCode: string | null;
  readonly variant: SurveyVariantView | null;
  readonly isTest: boolean;
  readonly campaignId: string | null;
  readonly campaignName: string | null;
  /** `+30***1391` for an owner or an admin; `null` for a member. Never the number whole. */
  readonly partyMasked: string | null;
  readonly answer: readonly SurveyGivenAnswerView[];
}

export interface SurveyRespondentPageView {
  readonly row: readonly SurveyRespondentRowView[];
  readonly nextCursor: string | null;
  readonly matchedCount: number;
}

export interface ConversationSurveyAnswerView {
  readonly conversationId: string;
  readonly outcome: SurveyRespondentOutcomeView;
  readonly variant: SurveyVariantView | null;
  readonly languageCode: string | null;
  readonly isTest: boolean;
  readonly answer: readonly {
    readonly questionCode: string;
    readonly instrumentCode: string;
    readonly questionKind: SurveyQuestionKindView;
    readonly variant: SurveyVariantView | null;
    readonly ordinal: number;
    /** In the language the answer was given in; `null` when the questionnaire has no such text. */
    readonly text: string | null;
    readonly answer: string;
    readonly mentioned: readonly string[];
    readonly optionLabel?: Readonly<Record<string, string>>;
    /** ADR 0053: an open answer's own words — only for a person who may read transcripts. */
    readonly openText?: string;
    readonly confident: boolean | null;
    readonly languageCode: string;
  }[];
}

export type SurveyDownloadFormatView = "csv" | "xlsx" | "json" | "pdf";

export interface SurveyDownloadRequestView {
  readonly voiceAgentId: string;
  /** The page's own filter, so the file is the page the person was looking at. */
  readonly filter: SurveyResultFilterView;
  readonly format: SurveyDownloadFormatView;
  readonly includeRecordingLink: boolean;
  readonly includeDeclinedAndNoAnswer: boolean;
  /** The person's portal display language: what a PDF report is worded in (AMENDED 2026-11-03). */
  readonly displayLanguage: "en" | "el";
}

export interface SurveyDownloadView {
  readonly downloadUrl: string;
  readonly fileExtension: string;
  readonly expiresAtText: string;
  /** The rows the file holds, as the API counted them in the same snapshot it wrote. */
  readonly rowCount: number;
  /** The name the file saves as (`wetalk-survey-….xlsx`), or `null` when the API did not name it. */
  readonly fileName: string | null;
}

// ---------------------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------------------

const SKIP_REASON_COPY = () => (new Map<string, string>([
  ["suppressed", portalText("shell", "on_your_do_not_call_list_5706970d")],
  ["already_surveyed", portalText("shell", "already_surveyed_answered_this_agent_s_survey_before_26e34f4d")],
  ["called_within_window", portalText("shell", "called_recently_015a835c")],
  ["no_answer_limit", portalText("shell", "no_answer_too_many_times_3681a35a")],
  ["outside_local_calling_hours", portalText("shell", "outside_their_calling_hours_3c954218")],
  ["unknown_time_zone", portalText("shell", "we_do_not_know_what_time_it_is_where_they_are_0136a508")],
  ["invalid_number", portalText("shell", "not_a_dialable_number_593d4fcb")],
]));

/** How a skipped row reads on the Campaign screen. An unknown reason reads as itself. */
export function skipReasonLabel(reason: string): string {
  return SKIP_REASON_COPY().get(reason) ?? reason;
}

const QUESTION_KIND_COPY = () => (new Map<string, string>([
  ["scale", portalText("shell", "scale_a29f0256")],
  ["choice", portalText("shell", "choice_0682be26")],
  ["open", portalText("shell", "open_cf9b7706")],
]));

export function questionKindLabel(kind: string): string {
  return QUESTION_KIND_COPY().get(kind) ?? kind;
}

const CONTACT_ROLE_COPY = () => (new Map<string, string>([
  ["display_name", portalText("shell", "name_709a2322")],
  ["e164", portalText("shell", "phone_number_8961d3bf")],
  ["language_code", portalText("shell", "preferred_language_1c7b0d77")],
  ["time_zone", portalText("shell", "time_zone_eea79afd")],
  ["personal_detail", portalText("shell", "personal_detail_29dc8d18")],
  ["ignored", portalText("shell", "ignore_98f55db5")],
]));

export function contactRoleLabel(role: string): string {
  return CONTACT_ROLE_COPY().get(role) ?? role;
}

/** What the "People already surveyed" note says under each of the three choices. */
const RETURNING_PERSON_COPY = () => (new Map<string, string>([
  [
    "same_questions",
    portalText("shell", "a_second_conversation_files_a_second_survey_against_the_sa_a92523a7"),
  ],
  [
    "different_questions",
    portalText("shell", "the_agent_recognises_the_number_greets_them_as_a_returning_a24819b8"),
  ],
  [
    "skip_them",
    // 2026-10-05: the rule the dialler applies (`already_surveyed`), stated rather than implied.
    portalText("shell", "people_who_answered_at_least_one_question_of_this_agent_s_0d47d9fc"),
  ],
]));

export function returningPersonNote(treatment: string): string {
  return RETURNING_PERSON_COPY().get(treatment) ?? treatment;
}

/**
 * What the concurrency slider says under it.
 *
 * At the ceiling the sentence changes, because the customer needs to know the campaign is
 * bounded by what the organization may hold at once (its concurrency entitlement, shared across every workspace and
 * agent — INT 2026-10-07, no longer "your tier") — and, either way, that inbound wins.
 */
export function concurrencyNote(simultaneousCall: number, ceiling: number): string {
  return simultaneousCall >= ceiling
    ? portalText("shell", "at_your_organization_s_limit_inbound_callers_still_take_pr_3718a29f")
    : portalText("shell", "inbound_callers_take_priority_so_the_campaign_uses_up_to_v_c04b3e7b", { v0: String(String(simultaneousCall)), v1: String(String(ceiling)) });
}

/** Monday-first short weekday labels, indexed by ISO weekday number. */
export const WEEKDAY_LABEL = () => ([
  "",
  portalText("shell", "mon_24b2a099"),
  portalText("shell", "tue_529541bb"),
  portalText("shell", "wed_23408b19"),
  portalText("shell", "thu_3593ccd9"),
  portalText("shell", "fri_bbd6e32e"),
  portalText("shell", "sat_6b782d41"),
  portalText("shell", "sun_48c98cab"),
]);

/** "Mon–Fri 10:00–19:00", built from the parts rather than stored as a sentence. */
export function callingWindowText(
  dayOfWeek: readonly number[],
  fromLocalTime: string,
  toLocalTime: string,
): string {
  if (dayOfWeek.length === 0) return portalText("shell", "no_days_selected_0a5db199");
  return `${weekdaysText(dayOfWeek)} ${fromLocalTime}–${toLocalTime}`;
}

/** Weekdays in words: one day, a run ("Mon–Fri"), or a list ("Mon, Wed, Fri"). */
function weekdaysText(dayOfWeek: readonly number[]): string {
  const sorted = [...dayOfWeek].sort((left, right) => left - right);
  const contiguous = sorted.every((one, index) => one === (sorted[0] ?? 0) + index);
  const first = WEEKDAY_LABEL()[sorted[0] ?? 0] ?? "";
  const last = WEEKDAY_LABEL()[sorted[sorted.length - 1] ?? 0] ?? "";
  return sorted.length === 1
    ? first
    : contiguous
      ? `${first}–${last}`
      : sorted.map((one) => WEEKDAY_LABEL()[one] ?? "").join(", ");
}

/**
 * One calling range (2026-10-30): an ISO weekday (Monday = 1), `HH:MM` to `HH:MM` (an end may be "24:00").
 * `@wetalk/view-model/calling-range`'s `CallingRange` (plan-034 VIEW-PKG), under the portal's name.
 */
export type CallingRangeView = CallingRange;

/**
 * Calling ranges in words (2026-10-30): days with the same ranges said together, as {@link callingWindowText} says
 * them, the ranges joined by commas, groups by " · " — "Mon–Fri 10:00–13:30, 18:00–20:30 · Sat 11:00–13:00". One
 * window on its days reads exactly as {@link callingWindowText}.
 */
export function callingHoursText(hours: readonly CallingRangeView[]): string {
  if (hours.length === 0) return portalText("shell", "no_days_selected_0a5db199");
  return callingRangeGroupsOf(hours)
    .map((group) => `${weekdaysText(group.dayOfWeek)} ${group.range.map((one) => `${one.fromLocalTime}–${one.toLocalTime}`).join(", ")}`)
    .join(" · ");
}

/**
 * One window on each of its days, as ranges (2026-10-30), and the ranges' summary as the API answers it beside them.
 * Moved to `@wetalk/view-model/calling-range` (plan-034 VIEW-PKG); re-exported here so no portal importer changes.
 */
export { windowRangeOf, windowSummaryOf } from "@wetalk/view-model/calling-range";

/** The sentence that keeps "whose clock?" from being ambiguous on every campaign screen. */
export const RECIPIENT_TIME_ZONE_NOTE =
  () => (portalText("shell", "in_the_person_s_own_time_zone_so_nobody_is_rung_at_breakfa_f877dc5c"));

/** A share as hundredths of a percent, so nothing on these screens is a float. */
export function basisPointOf(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.round((part / whole) * 10_000);
}

/** "49%" from 4,900 basis points. */
export function percentText(basisPoint: number): string {
  return `${String(Math.round(basisPoint / 100))}%`;
}

/** "4.2" from 42 tenths, without ever building a float from a division. */
export function tenthText(tenth: number): string {
  const whole = Math.trunc(tenth / 10);
  const fraction = Math.abs(tenth % 10);
  return `${String(whole)}.${String(fraction)}`;
}

// ---------------------------------------------------------------------------------------
// Analytics' outbound block (plan-031 VMD-WAPP, design §42.11; the API's `AnalyticsOutbound`)
// ---------------------------------------------------------------------------------------

/**
 * A VoiceAgent's answered outbound conversations in the Analytics window, and how many of them a voicemail answered
 * (detected and hung up on at once). Counts, never a share; both are part of the window's conversation count.
 */
export interface AnalyticsOutboundView {
  readonly answeredOutboundCount: number;
  readonly voicemailCount: number;
}

declare module "./gateway.js" {
  interface VoiceAgentAnalyticsView {
    /** plan-031: the outbound block. Absent (the in-memory gateway): not shown. */
    readonly outbound?: AnalyticsOutboundView;
  }
}

/** Analytics' `outbound`, both counts required (VMD-API). */
export function analyticsOutboundOfWire(value: unknown, path: string): AnalyticsOutboundView {
  const one = readRecord(value, path);
  return {
    answeredOutboundCount: readWholeNumber(one["answered_outbound_count"], `${path}.answered_outbound_count`),
    voicemailCount: readWholeNumber(one["voicemail_count"], `${path}.voicemail_count`),
  };
}
